import type { TFunction } from 'i18next';
import { COUNTDOWN_TIMER_WIDGET_TYPE } from '../../../utils/countdownTimerCodeUtils';
import {
  BOOLEAN_WIDGET_TYPE,
  CUSTOM_CALLABLE_WIDGET_TYPE,
  MECHANISM_WIDGET_TYPE,
  MOTOR_MULTI_WIDGET_TYPE,
  MOTOR_WIDGET_TYPE,
  OTHER_WIDGET_TYPE,
  isMotorMultiWidgetDataKey,
  motorBooleanWidgetDisplayTitle,
  motorDrawerBooleanMetricGroupTitle,
  motorDrawerFloatMetricGroupTitle,
  motorDrawerPortVariableTitle,
  motorFloatWidgetDisplayTitle,
  motorMultiWidgetDisplayTitle,
  mechanismFloatWidgetDisplayTitle,
  SENSOR_WIDGET_TYPE,
  chartTitleI18nKeyForStreamKey,
  widgetChartTitleI18nKey,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  isOtherElectronicsStreamSensorKey,
  motorPortDisplayTitle,
  otherMetricLabel,
  otherWidgetMetricDisplayTitle,
  sensorMetricLabel,
  sensorWidgetMetricDisplayTitle,
  streamMetricSlug,
  streamPortFromSensorKey,
  streamPrefixFromSensorKey,
} from '../../Graphs/sensor-utils';
import type { WidgetOptions } from '../dashboardTypes';

type TranslateFn = TFunction;

export type MotorMechanismWidgetTitleContext = {
  /** Effective drive-unit ports plotted on the widget (when known). */
  selectedMotorPorts?: readonly number[];
  /** Same labels as single-port motor drawer draggables (`Port N: variableName`, lead/follower, etc.). */
  motorPortLabel?: (port: number) => string | undefined;
  /** Same labels as single-port mechanism drawer draggables (`Port N: variableName`). */
  mechanismPortLabel?: (port: number) => string | undefined;
};

/** Display name for a custom callable stream: the `register_callable` parameter (`dataKey`). */
export function resolveCustomCallableDisplayName(parameter: string): string {
  return parameter;
}

/** True when project code still registers the stream parameter via `register_callable`. */
export function isCustomCallableStreamRegisteredInCode(parameter: string, registeredParameters: ReadonlySet<string>): boolean {
  return Boolean(parameter) && registeredParameters.has(parameter);
}

function resolveSelectedMotorPort(dataKey: string, selectedMotorPorts: readonly number[] | undefined): number {
  if (selectedMotorPorts?.length === 1) return selectedMotorPorts[0];
  return streamPortFromSensorKey(dataKey) ?? 0;
}

function fallbackMotorPortLabel(port: number, options?: WidgetOptions): string {
  if (options?.motorVariableName?.trim()) {
    return motorPortDisplayTitle(port, options.motorVariableName);
  }
  return motorPortDisplayTitle(port, '');
}

function fallbackMechanismPortLabel(port: number): string {
  return motorDrawerPortVariableTitle(port, '');
}

/**
 * Header title for motor/mechanism stream widgets (`motor`, `mechanism_data`, motor `boolean`).
 * Reuses drawer collapsible group titles; single-port widgets prepend the per-port draggable label.
 */
export function resolveMotorMechanismWidgetTitle(
  type: string,
  dataKey: string,
  t: TranslateFn,
  options: WidgetOptions | undefined,
  context?: MotorMechanismWidgetTitleContext
): string | undefined {
  const effectivePorts = context?.selectedMotorPorts ?? options?.selectedMotorPorts;
  const portsForMultiPortCheck = options?.selectedMotorPorts && options.selectedMotorPorts.length > 0 ? options.selectedMotorPorts : effectivePorts;
  const isMultiPort = Boolean(options?.allMotorPorts || (portsForMultiPortCheck && portsForMultiPortCheck.length > 1));
  const selectedMotorPorts = effectivePorts;

  if (type === MOTOR_WIDGET_TYPE && isMotorStreamSensorKey(dataKey)) {
    const metric = streamMetricSlug(dataKey);
    if (isMultiPort) {
      return motorDrawerFloatMetricGroupTitle(metric);
    }
    const port = resolveSelectedMotorPort(dataKey, selectedMotorPorts);
    const portLabel = context?.motorPortLabel?.(port) ?? fallbackMotorPortLabel(port, options);
    return motorFloatWidgetDisplayTitle(portLabel, metric);
  }

  if (type === MECHANISM_WIDGET_TYPE && isMotorStreamSensorKey(dataKey)) {
    const metric = streamMetricSlug(dataKey);
    const className = options?.mechanismClassName;
    if (!className) return undefined;
    if (isMultiPort) {
      return mechanismFloatWidgetDisplayTitle(undefined, className, metric, t);
    }
    const port = resolveSelectedMotorPort(dataKey, selectedMotorPorts);
    const portLabel = context?.mechanismPortLabel?.(port) ?? fallbackMechanismPortLabel(port);
    return mechanismFloatWidgetDisplayTitle(portLabel, className, metric, t);
  }

  if (type === BOOLEAN_WIDGET_TYPE && isMotorStreamSensorKey(dataKey)) {
    const metric = streamMetricSlug(dataKey);
    if (isMultiPort) {
      return motorDrawerBooleanMetricGroupTitle(metric);
    }
    const port = resolveSelectedMotorPort(dataKey, selectedMotorPorts);
    const portLabel = context?.motorPortLabel?.(port) ?? fallbackMotorPortLabel(port, options);
    return motorBooleanWidgetDisplayTitle(portLabel, metric);
  }

  return undefined;
}

export type SensorWidgetTitleContext = {
  /** Effective drive-unit ports plotted on the widget (when known). */
  selectedSensorPorts?: readonly number[];
  /** Same labels as single-port sensor drawer draggables (`Port N: variableName`). */
  sensorPortLabel?: (port: number) => string | undefined;
};

function resolveSelectedSensorPort(dataKey: string, selectedSensorPorts: readonly number[] | undefined): number {
  if (selectedSensorPorts?.length === 1) return selectedSensorPorts[0];
  return streamPortFromSensorKey(dataKey) ?? 0;
}

function fallbackSensorPortLabel(port: number, options?: WidgetOptions): string {
  if (options?.sensorVariableName?.trim()) {
    return motorPortDisplayTitle(port, options.sensorVariableName);
  }
  return motorPortDisplayTitle(port, '');
}

/**
 * Header title for electronic sensor stream widgets (`sensor`, sensor `boolean`).
 * Reuses drawer collapsible group titles; single-port widgets prepend the per-port draggable label.
 */
export function resolveSensorWidgetTitle(
  type: string,
  dataKey: string,
  options: WidgetOptions | undefined,
  context?: SensorWidgetTitleContext
): string | undefined {
  if (!isElectronicSensorStreamSensorKey(dataKey)) return undefined;

  if (type !== SENSOR_WIDGET_TYPE && type !== BOOLEAN_WIDGET_TYPE) return undefined;

  const prefix = streamPrefixFromSensorKey(dataKey);
  const metric = streamMetricSlug(dataKey);
  const effectivePorts = context?.selectedSensorPorts ?? options?.selectedSensorPorts;
  const portsForMultiPortCheck = options?.selectedSensorPorts?.length ? options.selectedSensorPorts : effectivePorts;
  const isMultiPort = Boolean(options?.allSensorPorts || (portsForMultiPortCheck && portsForMultiPortCheck.length > 1));
  const selectedSensorPorts = effectivePorts;

  if (isMultiPort) {
    return sensorMetricLabel(prefix, metric);
  }
  const port = resolveSelectedSensorPort(dataKey, selectedSensorPorts);
  const portLabel = context?.sensorPortLabel?.(port) ?? fallbackSensorPortLabel(port, options);
  return sensorWidgetMetricDisplayTitle(portLabel, prefix, metric);
}

export type OtherWidgetTitleContext = {
  /** Effective drive-unit ports plotted on the widget (when known). */
  selectedOtherPorts?: readonly number[];
  /** Same labels as single-port other drawer draggables (`Port N: variableName`). */
  otherPortLabel?: (port: number) => string | undefined;
};

function fallbackOtherPortLabel(port: number, options?: WidgetOptions): string {
  if (options?.otherVariableName?.trim()) {
    return motorPortDisplayTitle(port, options.otherVariableName);
  }
  return motorPortDisplayTitle(port, '');
}

/**
 * Header title for other-electronics stream widgets (`other`).
 * Reuses drawer collapsible group titles; single-port widgets prepend the per-port draggable label.
 */
export function resolveOtherWidgetTitle(
  type: string,
  dataKey: string,
  options: WidgetOptions | undefined,
  context?: OtherWidgetTitleContext
): string | undefined {
  if (!isOtherElectronicsStreamSensorKey(dataKey)) return undefined;
  if (type !== OTHER_WIDGET_TYPE) return undefined;

  const prefix = streamPrefixFromSensorKey(dataKey);
  const metric = streamMetricSlug(dataKey);
  const effectivePorts = context?.selectedOtherPorts ?? options?.selectedOtherPorts;
  const portsForMultiPortCheck = options?.selectedOtherPorts?.length ? options.selectedOtherPorts : effectivePorts;
  const isMultiPort = Boolean(options?.allOtherPorts || (portsForMultiPortCheck && portsForMultiPortCheck.length > 1));
  const selectedOtherPorts = effectivePorts;

  if (isMultiPort) {
    return otherMetricLabel(prefix, metric);
  }
  const port = resolveSelectedSensorPort(dataKey, selectedOtherPorts);
  const portLabel = context?.otherPortLabel?.(port) ?? fallbackOtherPortLabel(port, options);
  return otherWidgetMetricDisplayTitle(portLabel, prefix, metric);
}

/**
 * Synchronous default header title for a dashboard widget from layout `type` / `dataKey`.
 * Uses existing locale keys only (plus `widgetBase.unknown-widget` when type is missing).
 * Widget bodies may override via `useWidgetHeader` once mounted.
 */
export function defaultWidgetTitle(
  type: string,
  dataKey: string,
  t: TranslateFn,
  options?: WidgetOptions,
  motorMechanismContext?: MotorMechanismWidgetTitleContext
): string | undefined {
  if (!type?.trim()) {
    return t('widgetBase.unknown-widget', 'Widget');
  }
  if (type === 'orientation') {
    return t('widgetBase.drive-unit-orientation', 'Drive Unit Orientation');
  }
  if (type === 'field') {
    return t('dashboard.draggableComponent.title.field', 'Field Widget');
  }
  if (type === 'console') {
    return t('widgetBase.console', 'Console Logs');
  }
  if (type === 'timer') {
    return t('TimerWidget.title', 'Timer');
  }
  if (type === COUNTDOWN_TIMER_WIDGET_TYPE) {
    return t('CountdownTimerWidget.title', 'Countdown');
  }
  if (type === 'pos' && dataKey === 'all') {
    return t(chartTitleI18nKeyForStreamKey(type), type);
  }
  if (type === 'speed') {
    return t('SpeedGaugeWidget.title', 'Speed');
  }
  if (type === CUSTOM_CALLABLE_WIDGET_TYPE) {
    return resolveCustomCallableDisplayName(dataKey) || t('widgetBase.unknown-widget', 'Widget');
  }
  if (type === MOTOR_MULTI_WIDGET_TYPE && isMotorMultiWidgetDataKey(dataKey)) {
    if (options?.motorVariableName) return options.motorVariableName;
    const port = options?.selectedMotorPort ?? streamPortFromSensorKey(`${dataKey}.velocity`) ?? 0;
    return motorMultiWidgetDisplayTitle(port);
  }

  const motorMechanismTitle = resolveMotorMechanismWidgetTitle(type, dataKey, t, options, motorMechanismContext);
  if (motorMechanismTitle !== undefined) {
    return motorMechanismTitle;
  }

  const sensorTitle = resolveSensorWidgetTitle(type, dataKey, options);
  if (sensorTitle !== undefined) {
    return sensorTitle;
  }

  const otherTitle = resolveOtherWidgetTitle(type, dataKey, options);
  if (otherTitle !== undefined) {
    return otherTitle;
  }

  if (dataKey === 'all') {
    return t(chartTitleI18nKeyForStreamKey(type), type);
  }
  return t(widgetChartTitleI18nKey(type, dataKey), `${type}.${dataKey}`);
}
